/**
 * t335u item 3: recurring tasks like checklist schedules. The rule lives on the newest task in a series; the next one
 * is created when this one is done (default) or on this one's due date ('schedule'). Shared by the API (next dates),
 * desktop, phone web and native (the Repeat form and the muted "Repeats …" line).
 */
import { DateTime } from 'luxon';
import type { RecurrenceRule } from '../types';

export const REPEAT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const NTH = ['', 'first', 'second', 'third', 'fourth'];

const nthWeekday = (year: number, month: number, weekday: number, pos: number): DateTime | null => {
  const first = DateTime.fromObject({ year, month, day: 1 });
  if (pos === -1) {
    const last = first.endOf('month').startOf('day');
    return last.minus({ days: (last.weekday - weekday + 7) % 7 });
  }
  const d = first.plus({ days: (weekday - first.weekday + 7) % 7 + (pos - 1) * 7 });
  return d.month === month ? d : null;
};
const onMonthDay = (m: DateTime, day: number) => (day === -1 ? m.endOf('month').startOf('day') : m.set({ day: Math.min(day, m.daysInMonth ?? 28) }));

/** Next date (YYYY-MM-DD) strictly after `from`, or null when the series has ended (until / count). */
export function nextOccurrence(rule: RecurrenceRule, from: string): string | null {
  const interval = Math.max(1, Math.floor(rule.interval || 1));
  if (rule.count && (rule.seq ?? 1) >= rule.count) return null;
  const base = DateTime.fromISO(from);
  let next: DateTime | null = null;
  if (rule.freq === 'daily') next = base.plus({ days: interval });
  else if (rule.freq === 'monthly') {
    if (rule.bysetpos && rule.byweekday?.length) {
      for (let k = 0; k < 24 && !next; k++) {
        const step = base.startOf('month').plus({ months: interval * k });
        const d = nthWeekday(step.year, step.month, rule.byweekday[0], rule.bysetpos);
        if (d && d > base) next = d;
      }
    } else if (rule.bymonthday) next = onMonthDay(base.startOf('month').plus({ months: interval }), rule.bymonthday);
    else next = base.plus({ months: interval });
  } else if (rule.freq === 'weekly') {
    const days = (rule.byweekday?.length ? rule.byweekday : [base.weekday]).filter(d => d >= 1 && d <= 7);
    const baseWeek = base.startOf('week');
    for (let i = 1; i <= 7 * interval + 7; i++) {
      const d = base.plus({ days: i });
      const weeksApart = Math.round(d.startOf('week').diff(baseWeek, 'weeks').weeks);
      if (days.includes(d.weekday) && weeksApart % interval === 0) { next = d; break; }
    }
  }
  if (!next) return null;
  if (rule.until && next.toISODate()! > rule.until) return null;
  return next.toISODate();
}

/** The rule the next instance carries (its number in the series goes up by one). */
export const nextRule = (rule: RecurrenceRule): RecurrenceRule => ({ ...rule, seq: (rule.seq ?? 1) + 1 });

export function isValidRule(rule: unknown): rule is RecurrenceRule {
  if (!rule || typeof rule !== 'object') return false;
  const r = rule as RecurrenceRule;
  const int = (v: unknown, lo: number, hi: number) => Number.isInteger(v) && (v as number) >= lo && (v as number) <= hi;
  if (!['daily', 'weekly', 'monthly'].includes(r.freq)) return false;
  if (!int(r.interval, 1, 365)) return false;
  if (r.byweekday && (!Array.isArray(r.byweekday) || r.byweekday.some(d => !int(d, 1, 7)))) return false;
  if (r.until && !/^\d{4}-\d{2}-\d{2}$/.test(r.until)) return false;
  if (r.bymonthday !== undefined && r.bymonthday !== -1 && !int(r.bymonthday, 1, 31)) return false;
  if (r.bysetpos !== undefined && r.bysetpos !== -1 && !int(r.bysetpos, 1, 4)) return false;
  if (r.bysetpos !== undefined && !r.byweekday?.length) return false;
  if (r.count !== undefined && !int(r.count, 1, 999)) return false;
  if (r.seq !== undefined && !int(r.seq, 1, 100000)) return false;
  if (r.create !== undefined && r.create !== 'done' && r.create !== 'schedule') return false;
  return true;
}

const list = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;

/** "Every week on Mon and Wed", "Every month on the second Tuesday", "Every 2 days". */
export function recurrencePattern(rule: RecurrenceRule, anchor?: string | null): string {
  const n = Math.max(1, rule.interval || 1);
  const unit = { daily: 'day', weekly: 'week', monthly: 'month' }[rule.freq];
  const every = n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`;
  if (rule.freq === 'weekly') {
    const days = (rule.byweekday?.length ? [...rule.byweekday].sort() : anchor ? [DateTime.fromISO(anchor).weekday] : []).map(d => REPEAT_DAYS[d - 1]);
    return days.length ? `${every} on ${list(days)}` : every;
  }
  if (rule.freq === 'monthly') {
    if (rule.bysetpos && rule.byweekday?.length) return `${every} on the ${rule.bysetpos === -1 ? 'last' : NTH[rule.bysetpos]} ${DAY_NAMES[rule.byweekday[0] - 1]}`;
    const day = rule.bymonthday ?? (anchor ? DateTime.fromISO(anchor).day : null);
    if (day === -1) return `${every} on the last day`;
    return day ? `${every} on the ${ordinal(day)}` : every;
  }
  return every;
}

/** The muted line under a recurring task: "Repeats every week on Mon · 3 more · next when done". */
export function recurrenceText(rule: RecurrenceRule, anchor?: string | null): string {
  const bits = [recurrencePattern(rule, anchor).replace(/^Every/, 'Repeats every')];
  if (rule.count) { const left = rule.count - (rule.seq ?? 1); bits.push(left > 0 ? `${left} more` : 'last one'); }
  else if (rule.until) bits.push(`until ${DateTime.fromISO(rule.until).toFormat('d LLL yyyy')}`);
  if (rule.create === 'schedule') bits.push('next on its date');
  return bits.join(' · ');
}

// ------------------------------------------------------------------------------------------------ the Repeat form
export type RepeatFreq = '' | RecurrenceRule['freq'];
export type RepeatMonthly = 'date' | 'weekday' | 'last';
export type RepeatEnds = 'never' | 'on' | 'after';
export interface RepeatForm { freq: RepeatFreq; interval: string; days: number[]; monthly: RepeatMonthly; ends: RepeatEnds; until: string; count: string; create: 'done' | 'schedule' }

export const REPEAT_FREQ_OPTIONS: { id: RepeatFreq; name: string }[] = [{ id: '', name: 'Does not repeat' }, { id: 'daily', name: 'Daily' }, { id: 'weekly', name: 'Weekly' }, { id: 'monthly', name: 'Monthly' }];
export const REPEAT_ENDS_OPTIONS: { id: RepeatEnds; name: string }[] = [{ id: 'never', name: 'Never' }, { id: 'on', name: 'On a date' }, { id: 'after', name: 'After a number of times' }];
export const REPEAT_CREATE_OPTIONS: { id: 'done' | 'schedule'; name: string }[] = [{ id: 'done', name: 'When this one is done' }, { id: 'schedule', name: 'On its date, done or not' }];
export const REPEAT_NOTE = 'The next one appears when this one is done, so an unfinished task never piles up. Choose “On its date” for a new one every time regardless.';
export const repeatUnit = (f: RepeatFreq, n: number) => (f ? `${{ daily: 'day', weekly: 'week', monthly: 'month' }[f]}${n === 1 ? '' : 's'}` : '');

/** The monthly choices for a due date: "On the 14th", "On the second Tuesday", "On the last day". */
export function repeatMonthlyOptions(anchor: string): { id: RepeatMonthly; name: string }[] {
  const d = DateTime.fromISO(anchor);
  const pos = Math.ceil(d.day / 7), last = d.plus({ weeks: 1 }).month !== d.month;
  return [
    { id: 'date', name: `On the ${ordinal(d.day)}` },
    { id: 'weekday', name: `On the ${pos >= 5 || (last && pos === 4) ? 'last' : NTH[pos]} ${DAY_NAMES[d.weekday - 1]}` },
    { id: 'last', name: 'On the last day' },
  ];
}

export function repeatForm(rule: RecurrenceRule | null | undefined, anchor: string): RepeatForm {
  const d = DateTime.fromISO(anchor);
  return {
    freq: rule?.freq ?? '', interval: String(rule?.interval ?? 1),
    days: rule?.byweekday?.length ? [...rule.byweekday] : [d.weekday],
    monthly: rule?.bysetpos ? 'weekday' : rule?.bymonthday === -1 ? 'last' : 'date',
    ends: rule?.count ? 'after' : rule?.until ? 'on' : 'never', until: rule?.until ?? '', count: String(rule?.count ?? 5),
    create: rule?.create ?? 'done',
  };
}

export function repeatProblem(f: RepeatForm, hasDue: boolean): string | null {
  if (!f.freq) return null;
  const n = Number(f.interval);
  if (!Number.isInteger(n) || n < 1 || n > 365) return 'Every: a number from 1 to 365.';
  if (f.freq === 'weekly' && !f.days.length) return 'Choose at least one day.';
  if (f.ends === 'on' && !/^\d{4}-\d{2}-\d{2}$/.test(f.until)) return 'Ends: choose the date.';
  if (f.ends === 'after') { const c = Number(f.count); if (!Number.isInteger(c) || c < 2 || c > 999) return 'Ends after: a number from 2 to 999.'; }
  if (f.create === 'schedule' && !hasDue) return 'On its date needs a due date.';
  return null;
}

/** The rule to save (null = does not repeat). Keeps `seq` so "after N times" counts from where the series is. */
export function repeatRule(f: RepeatForm, anchor: string, prev?: RecurrenceRule | null): RecurrenceRule | null {
  if (!f.freq) return null;
  const d = DateTime.fromISO(anchor);
  const r: RecurrenceRule = { freq: f.freq, interval: Math.max(1, Number(f.interval) || 1) };
  if (f.freq === 'weekly') r.byweekday = [...f.days].sort((a, b) => a - b);
  if (f.freq === 'monthly') {
    if (f.monthly === 'last') r.bymonthday = -1;
    else if (f.monthly === 'weekday') { const pos = Math.ceil(d.day / 7); r.bysetpos = pos >= 5 || (pos === 4 && d.plus({ weeks: 1 }).month !== d.month) ? -1 : pos; r.byweekday = [d.weekday]; }
    else r.bymonthday = d.day;
  }
  if (f.ends === 'on' && f.until) r.until = f.until;
  if (f.ends === 'after') { r.count = Number(f.count); if (prev?.seq) r.seq = prev.seq; }
  if (f.create === 'schedule') r.create = 'schedule';
  return r;
}

/** The next few dates for the form's preview line. */
export function repeatPreview(rule: RecurrenceRule | null, anchor: string, n = 3): string[] {
  if (!rule) return [];
  const out: string[] = [];
  let r = rule, from = anchor;
  for (let i = 0; i < n; i++) { const next = nextOccurrence(r, from); if (!next) break; out.push(next); from = next; r = nextRule(r); }
  return out;
}
